import { applyOps, createLayerHistory, type LayerHistory } from "#canvas/history.ts";
import { type LayerRender, paintLayer } from "#canvas/renderers.ts";
import type { Layer, PixelOp } from "#canvas/types.ts";
import { useCallback, useEffect, useRef } from "react";

interface LiveLayersControls {
  /** Read the current "live" layer list (mid-stroke or post-commit). */
  getLive: () => Layer[];
  /** Mutate the live active layer in place, repaint its renderer. */
  applyLiveOps: (ops: PixelOp[]) => void;
  /** Snapshot current live state into history, fire onCommit. */
  commit: () => void;
  /** Discard in-progress live edits, snap back to last committed state. No history push, no onCommit. */
  discardLive: () => void;
  /** Replace all layers (push history, repaint all). Does NOT fire onCommit. */
  setAll: (next: Layer[]) => void;
  /** Step one history entry back. Fires onCommit if a step was taken. */
  undo: () => void;
  /** Step one history entry forward. Fires onCommit if a step was taken. */
  redo: () => void;
}

interface Args {
  layers: Layer[];
  activeLayerId: string;
  renderers: Map<string, LayerRender>;
  onCommit: (layers: Layer[]) => void;
}

/**
 * Owns the "live" layer state and undo/redo history. Live state is mutated
 * in-place during a stroke for performance; commits snapshot it into history
 * and fire onCommit so the parent's controlled state catches up.
 *
 * Repaints are pushed directly to the renderer Map (out-of-band, bypassing
 * React reconciliation) so pixi sees only dirty-source updates.
 *
 * `lastExternalRef` tracks the most recent prop value we've already absorbed,
 * so external echoes from `onCommit` don't double-push history.
 *
 * @param args.layers Controlled layer list from props.
 * @param args.activeLayerId Layer that `applyLiveOps` mutates.
 * @param args.renderers Layer-id → offscreen surface map. Used for repainting.
 * @param args.onCommit Fired after `commit`, `undo`, `redo` (NOT `setAll`).
 */
export function useLiveLayers({ layers, activeLayerId, renderers, onCommit }: Args): LiveLayersControls {
  const liveLayersRef = useRef<Layer[]>(layers);
  const lastExternalRef = useRef<Layer[]>(layers);
  const historyRef = useRef<LayerHistory>(createLayerHistory(layers));

  useEffect(() => {
    if (layers === lastExternalRef.current) return;
    lastExternalRef.current = layers;
    liveLayersRef.current = layers;
    historyRef.current.pushIfChanged(layers);
  }, [layers]);

  const repaintAll = useCallback((next: Layer[]): void => {
    for (const layer of next) {
      const r = renderers.get(layer.id);
      if (r) paintLayer(r, layer.data);
    }
  }, [renderers]);

  const applyLiveOps = useCallback((ops: PixelOp[]): void => {
    const live = liveLayersRef.current;
    const idx = live.findIndex((l) => l.id === activeLayerId);
    if (idx === -1) return;
    const layer = live[idx];
    if (!layer) return;
    const next = applyOps(layer, ops);
    const newLive = live.slice();
    newLive[idx] = next;
    liveLayersRef.current = newLive;
    const r = renderers.get(activeLayerId);
    if (r) paintLayer(r, next.data);
  }, [activeLayerId, renderers]);

  const commit = useCallback((): void => {
    const next = liveLayersRef.current;
    historyRef.current.push(next);
    lastExternalRef.current = next;
    onCommit(next);
  }, [onCommit]);

  const discardLive = useCallback((): void => {
    const restored = lastExternalRef.current;
    liveLayersRef.current = restored;
    repaintAll(restored);
  }, [repaintAll]);

  const setAll = useCallback((next: Layer[]): void => {
    liveLayersRef.current = next;
    lastExternalRef.current = next;
    historyRef.current.push(next);
    repaintAll(next);
  }, [repaintAll]);

  const undo = useCallback((): void => {
    const restored = historyRef.current.undo();
    if (!restored) return;
    liveLayersRef.current = restored;
    lastExternalRef.current = restored;
    repaintAll(restored);
    onCommit(restored);
  }, [onCommit, repaintAll]);

  const redo = useCallback((): void => {
    const restored = historyRef.current.redo();
    if (!restored) return;
    liveLayersRef.current = restored;
    lastExternalRef.current = restored;
    repaintAll(restored);
    onCommit(restored);
  }, [onCommit, repaintAll]);

  const getLive = useCallback((): Layer[] => liveLayersRef.current, []);

  return { getLive, applyLiveOps, commit, discardLive, setAll, undo, redo };
}
